/**
 * Google Analytics 4 helper for tracking conversion events.
 * GA4 Measurement ID: G-VGGF25FPKQ (loaded in public/index.html)
 *
 * All events are safe to call even if gtag hasn't loaded yet —
 * window.dataLayer queues them.
 */

const isGtagReady = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Track a custom event in GA4.
 * @param {string} eventName - Standard or custom event name (snake_case)
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagReady()) {
    // Queue the event in dataLayer if gtag isn't ready yet
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
    }
    return;
  }
  try {
    window.gtag('event', eventName, params);
  } catch (e) {
    // Silent fail — analytics should never break the app
    console.debug('[analytics] event failed:', eventName, e);
  }
};

// ----- Conversion event helpers (named for the funnel they belong to) -----

/** User completed Google OAuth signup (new user created). */
export const trackSignup = (method = 'google') => {
  trackEvent('sign_up', { method });
};

/** User clicked the Run Audit button (intent signal). */
export const trackAuditStarted = () => {
  trackEvent('audit_started', { tool: 'future_proof_audit' });
};

/** Audit successfully generated and report shown to user. */
export const trackAuditCompleted = () => {
  trackEvent('audit_completed', { tool: 'future_proof_audit', value: 5 });
};

/** User clicked print/save-as-PDF on the audit report. */
export const trackAuditPrinted = () => {
  trackEvent('audit_printed', { tool: 'future_proof_audit' });
};

/** User submitted email to download the lead-magnet checklist. */
export const trackChecklistSubscribed = () => {
  trackEvent('generate_lead', { method: 'checklist_subscribe' });
};

/** User clicked a Stripe checkout link / purchase button. */
export const trackBeginCheckout = (plan, value) => {
  trackEvent('begin_checkout', { plan, value, currency: 'USD' });
};

/** Stripe checkout completed (set up via webhook if you want server-side accuracy). */
export const trackPurchase = (plan, value) => {
  trackEvent('purchase', { plan, value, currency: 'USD' });
};
